import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs';

import { PortalRoutes } from '@app/app-routing.module';
import { ApiService } from '@app/services/api/api.service';
import {
  PortalAutoQuoteNode,
  PortalAutoQuoteOptionNode,
  PortalAutoQuoteStatus,
  PortalTripType,
} from '@app/services/api/api.types';
import type { GoogleFlightOption } from '@app/services/google-flights/google-flights.types';
import { PortalAutoQuoteContextService } from '@app/services/portal-auto-quote/portal-auto-quote-context.service';
import { Tracking } from '@app/services/tracking/tracking.service';
import { portalAutoQuoteOptionToGoogleFlightOption } from '@app/utils/portal-auto-quote-option-to-google-flight.util';
import { PortalTrackingEvents } from '@app/utils/tracking-events';

@Component({
  selector: 'quote-page',
  templateUrl: './quote-page.component.html',
  styleUrls: ['./quote-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuotePageComponent implements OnInit {
  PortalTripType = PortalTripType;

  apiService = inject(ApiService);
  router = inject(Router);
  route = inject(ActivatedRoute);
  autoQuoteContext = inject(PortalAutoQuoteContextService);
  destroyRef = inject(DestroyRef);
  formBuilder = inject(FormBuilder);

  ready = signal(false);
  submitting = signal(false);
  autoQuote$ = this.autoQuoteContext.getAutoQuote$();

  quoteToken = '';

  noteForm = this.formBuilder.group({
    noteControl: [''],
  });

  ngOnInit() {
    const token = this.route.snapshot.paramMap.get(PortalRoutes.QuoteToken) ?? '';

    if (!token) {
      this.router.navigate(['/', PortalRoutes.NotFound]).catch(() => undefined);
      return;
    }

    this.quoteToken = token;
    this.autoQuoteContext.clear();

    this.apiService
      .portalAutoQuote({ token })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ portalAutoQuote, errors }) => {
          if (errors?.length || !portalAutoQuote) {
            void this.router.navigate(['/', PortalRoutes.NotFound], {
              queryParamsHandling: 'merge',
            });

            return;
          }

          const quote = portalAutoQuote;

          if (quote.status === PortalAutoQuoteStatus.NOT_FOUND) {
            void this.router.navigate(['/', PortalRoutes.NotFound], {
              queryParamsHandling: 'merge',
            });

            return;
          }

          if (quote.expired || quote.status === PortalAutoQuoteStatus.EXPIRED) {
            this.autoQuoteContext.setAutoQuote(quote);
            void this.router.navigate(['/', PortalRoutes.Quote, PortalRoutes.QuoteExpired], {
              queryParamsHandling: 'merge',
            });

            return;
          }

          this.applyQuoteToForm(quote);
          this.autoQuoteContext.setAutoQuote(quote);
          this.ready.set(true);
          Tracking.track(PortalTrackingEvents.portalAutoQuotePageOpened);
        },
        error: () => {
          void this.router.navigate(['/', PortalRoutes.NotFound], { queryParamsHandling: 'merge' });
        },
      });
  }

  noteSubmit() {
    if (this.noteForm.disabled || this.submitting()) {
      return;
    }

    const text = (this.noteForm.get('noteControl')?.value ?? '').trim();

    Tracking.track(PortalTrackingEvents.portalAutoQuoteShareWithExpertPressed);

    this.submitting.set(true);

    this.apiService
      .submitPortalAutoQuoteAdditionalFeedback({ input: { token: this.quoteToken, text } })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.submitting.set(false)),
      )
      .subscribe({
        next: (result) => {
          if (!result.ok || !result.autoQuote) {
            return;
          }

          this.autoQuoteContext.setAutoQuote(result.autoQuote);

          void this.router.navigate(['/', PortalRoutes.Quote, PortalRoutes.QuoteThankYou], {
            queryParamsHandling: 'merge',
          });
        },
      });
  }

  applyQuoteToForm(quote: PortalAutoQuoteNode) {
    this.noteForm.enable({ emitEvent: false });
    this.noteForm.patchValue({ noteControl: quote.additionalFeedback ?? '' }, { emitEvent: false });
  }

  flightDisplayOption(
    quote: PortalAutoQuoteNode,
    option: PortalAutoQuoteOptionNode,
  ): GoogleFlightOption {
    return portalAutoQuoteOptionToGoogleFlightOption(quote, option);
  }
}
